import * as msal from '@azure/msal-browser';
import { azure } from 'config';
import jwt from 'jsonwebtoken';
import type { JWTToken, UserType } from 'lib/types';

/**
 * Configuration for the MSAL library
 */
const ssoConfig = {
  auth: {
    clientId: azure.clientId,
    authority: `https://login.microsoftonline.com/${azure.tenant}`,
    redirectUri: '/',
    postLogoutRedirectUri: '/login',
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
    secureCookies: process.env.NODE_ENV === 'production',
  },
};

/**
 * Login scopes for the MSAL library
 */
export const loginRequest = {
  scopes: [`${azure.clientId}/.default`],
};

export const SSOInstance = new msal.PublicClientApplication(ssoConfig);

/**
 * Get Azure AD active account
 * if any account is active will try to set one
 * @returns msal.AccountInfo
 */
const getActiveAccount = () => {
  let account: msal.AccountInfo | null | undefined =
    SSOInstance.getActiveAccount();

  if (account) {
    return account;
  }
  const accounts = SSOInstance.getAllAccounts();
  account = accounts.find(
    (acc) =>
      acc.idTokenClaims && (acc.idTokenClaims as any).aud === azure.clientId,
  );

  if (!account) {
    throw Error('No active account! Verify a user has been signed in.');
  }

  SSOInstance.setActiveAccount(account);
  return account;
};

/**
 * Get Token from Azure AD
 * @returns string
 */
export const getToken = async () => {
  const account = getActiveAccount();
  const { accessToken } = await SSOInstance.acquireTokenSilent({
    ...loginRequest,
    account,
  });

  return accessToken;
};

/**
 * Get user information
 * @returns UserType
 */
export const userInfo = async (): Promise<UserType> => {
  const accessToken = await getToken();
  const decodedToken = jwt.decode(accessToken) as JWTToken;
  const short = decodedToken.name
    .split(' ')
    .map((n) => n[0])
    .join('');

  return {
    displayName: decodedToken.name,
    givenName: decodedToken.given_name,
    surname: decodedToken.family_name,
    short,
    mail: decodedToken.email,
  };
};
